import {Sine} from "gsap";
import {Random} from "html-living-framework";
import {array} from "../Utils/array";
import {Container} from "../Utils/Container";
import {Sprite} from "../Utils/Sprite";
import {PuppetType} from "./PuppetType";

export class PuppetItem extends Container {

    public readonly sprites = array(() => this.addChild(new Sprite()));
    public readonly puppetType: PuppetType;
    private _value = 0;
    private _isMoving = false;
    private _initX = 0;
    private readonly _direction: number;

    constructor(puppetType: PuppetType) {
        super();
        this.puppetType = puppetType;
        this._direction =
            puppetType === PuppetType.Character ? 1 :
                puppetType === PuppetType.Prop ? -1 :
                    0;
    }

    public onLayout() {
        this.sprites.forEach((it) => it.visible = false);
        this._initX = this.x;
    }

    get value(): number {
        return this._value;
    }

    set value(value: number) {
        if (this._value !== value) {
            if (value < 0) {
                value = 0;
            }
            if (value > this.sprites.length) {
                value = value % this.sprites.length;
            }
            this._value = value;
            this.sprites.forEach((it, i) => it.visible = i === value - 1);
        }
    }

    get maxValue() {
        return this.sprites.length;
    }

    public startMove(minX: number, maxX: number) {
        if (this._isMoving) {
            return;
        }
        this._isMoving = true;
        const bounds = this.getLocalBounds();
        minX -= bounds.left;
        maxX -= bounds.right;
        switch (this.puppetType) {
            case PuppetType.Background:
                minX = Math.max(minX, this.x - 50);
                maxX = Math.min(maxX, this.x + 50);
                break;
            case PuppetType.Prop:
                if (this.x > 0) {
                    minX = Math.max(minX, 0);
                    maxX = this._initX;
                } else {
                    minX = this._initX;
                    maxX = Math.min(maxX, 0);
                }
                break;
        }
        this.killTimelines();
        this.doMove(minX, maxX);
    }

    public stopMove() {
        this._isMoving = false;
    }

    private doMove(minX: number, maxX: number) {
        if (!this._isMoving) {
            return;
        }
        const x = Random.floatBetween(minX, maxX);
        this.doStep(x, minX, maxX);
    }

    private doTurn(cb: () => void) {
        if (!this._isMoving) {
            return;
        }
        this.newTimeline().to(this.scale, 0.6, {x: -this.scale.x, ease: Sine.easeInOut}).call(cb);
    }

    private doStep(x: number, minX: number, maxX: number) {
        if (!this._isMoving) {
            return;
        }
        const dx = x - this.x;
        const adx = Math.abs(dx);
        let minStep = 20;
        let maxStep = 30;
        switch (this.puppetType) {
            case PuppetType.Character:
                minStep = 80;
                maxStep = 160;
                break;
            case PuppetType.Prop:
                minStep = 50;
                maxStep = 100;
                break;
        }
        if (adx < minStep) {
            this.newTimeline().call(() => this.doMove(minX, maxX), undefined, undefined, 0.1);
            return;
        }
        const ds = this.scale.x * this._direction;
        if (dx < 0 && ds > 0 || dx > 0 && ds < 0) {
            this.doTurn(() => this.doStep(x, minX, maxX));
            return;
        }
        let step = Random.floatBetween(minStep, maxStep);
        if (adx < step) {
            step = adx;
        }
        if (dx < 0) {
            step = -step;
        }
        const tx = this.x + step;
        const ty = this.y - Random.floatBetween(70, 100);
        const t = 0.3;
        const r = Random.intBetween(-1, 2) * 4 * Math.PI / 180;
        this.newTimeline()
            .to(this, t, {rotation: r, ease: Sine.easeIn}, 0)
            .to(this, t, {rotation: 0, ease: Sine.easeOut})
            .to(this, t, {x: tx, ease: Sine.easeInOut}, 0)
            .to(this, t, {y: ty, ease: Sine.easeInOut, repeat: 1, yoyo: true}, 0)
            .call(() => {
                if (!this._isMoving) {
                    return;
                }
                if (Math.abs(x - this.x) < minStep) {
                    this.newTimeline().call(() => this.doMove(minX, maxX),
                        undefined, undefined, Random.floatBetween(0.2, 0.6));
                } else {
                    this.doStep(x, minX, maxX);
                }
            });
    }
}
